import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Form, FormField, FormItem, FormControl, FormMessage } from "@/components/ui/form";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertCircle, Loader2, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/useAuth";
import { useSafeBack } from "@/lib/back";

const REMEMBER_EMAIL_KEY = "mobcash_remembered_email";

const formSchema = z.object({
    email: z.string().email("Email invalide"),
    password: z.string().min(6, "Le mot de passe doit contenir au moins 6 caractères"),
    remember: z.boolean().optional(),
});

type FormValues = z.infer<typeof formSchema>;

function LoginPage() {
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const goBack = useSafeBack("/");
    const [error, setError] = useState<string | null>(null);
    const [showPassword, setShowPassword] = useState(false);
    const { login } = useAuth();
    const emailInputRef = useRef<HTMLInputElement>(null);

    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            email: localStorage.getItem(REMEMBER_EMAIL_KEY) || "",
            password: "",
            remember: !!localStorage.getItem(REMEMBER_EMAIL_KEY),
        },
    });

    useEffect(() => {
        emailInputRef.current?.focus();
    }, []);

    const onSubmit = async (data: FormValues) => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await login(data.email, data.password);

            if (result.success) {
                if (data.remember) {
                    localStorage.setItem(REMEMBER_EMAIL_KEY, data.email);
                } else {
                    localStorage.removeItem(REMEMBER_EMAIL_KEY);
                }
                navigate("/parrainage", { replace: true });
            } else {
                setError(result.error || "Une erreur est survenue lors de la connexion");
            }
        } catch (err: any) {
            setError(
                err.message ||
                "Une erreur est survenue lors de la connexion"
            );
            console.error("Erreur de connexion :", err.message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="relative flex min-h-[100dvh] flex-col bg-background text-foreground [padding-top:env(safe-area-inset-top)] [padding-bottom:env(safe-area-inset-bottom)]">
            <div
                aria-hidden
                className="pointer-events-none fixed inset-0 -z-10"
                style={{
                    background:
                        "radial-gradient(120% 80% at 50% 0%, var(--page-gradient-via) 0%, var(--page-gradient-from) 45%, var(--page-gradient-to) 100%)",
                }}
            />

            <header className="sticky top-0 z-30 border-b border-border bg-header-bg backdrop-blur-xl">
                <div className="mx-auto flex max-w-[52ch] items-center gap-3 px-4 py-3 lg:max-w-6xl lg:px-8">
                    <button
                        type="button"
                        onClick={goBack}
                        aria-label="Retour"
                        className="grid size-9 place-items-center rounded-full bg-muted ring-1 ring-card-muted-ring"
                    >
                        <ArrowLeft className="size-4" />
                    </button>
                    <h1 className="text-base font-semibold tracking-tight">Connexion</h1>
                </div>
            </header>

            <main className="mx-auto flex w-full max-w-[52ch] flex-1 flex-col items-center justify-center px-4 py-8 lg:max-w-6xl lg:px-8">
                <div className="w-full max-w-sm">
                    <div className="mb-6 text-center">
                        <h2 className="text-2xl font-semibold tracking-tight">Bon retour</h2>
                        <p className="mt-1 text-sm text-muted-foreground">Entrez vos informations pour vous connecter</p>
                    </div>

                    {error && (
                        <div
                            role="alert"
                            aria-live="polite"
                            className="mb-4 rounded-xl border border-destructive/40 bg-destructive/10 p-3.5 text-sm text-destructive"
                        >
                            <p className="flex items-start gap-2 font-semibold">
                                <AlertCircle className="mt-0.5 size-4 shrink-0" />
                                Échec d'authentification
                            </p>
                            <p className="mt-1 opacity-90">{error}</p>
                        </div>
                    )}

                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                            <FormField
                                control={form.control}
                                name="email"
                                render={({ field }) => (
                                    <FormItem>
                                        <Label htmlFor="email" className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Email</Label>
                                        <FormControl>
                                            <Input
                                                id="email"
                                                type="email"
                                                placeholder="m@example.com"
                                                required
                                                autoComplete="email"
                                                className="h-auto w-full rounded-xl bg-surface px-4 py-3 text-base text-foreground ring-1 ring-border outline-none transition focus:ring-foreground/20"
                                                {...field}
                                                ref={(el) => {
                                                    field.ref(el);
                                                    emailInputRef.current = el;
                                                }}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="password"
                                render={({ field }) => (
                                    <FormItem>
                                        <div className="flex items-center justify-between">
                                            <Label htmlFor="password" className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Mot de passe</Label>
                                            <Link
                                                to="/forgot-password"
                                                className="text-[11px] font-medium text-muted-foreground underline underline-offset-2 hover:text-foreground"
                                            >
                                                Mot de passe oublié ?
                                            </Link>
                                        </div>
                                        <FormControl>
                                            <div className="relative">
                                                <Input
                                                    id="password"
                                                    type={showPassword ? "text" : "password"}
                                                    required
                                                    autoComplete="current-password"
                                                    className="w-full rounded-xl bg-surface px-4 py-3 pr-10 text-base text-foreground ring-1 ring-border outline-none transition focus:ring-foreground/20"
                                                    {...field}
                                                />
                                                <button
                                                    type="button"
                                                    aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-text-subtle hover:text-foreground"
                                                    onClick={() => setShowPassword((v) => !v)}
                                                >
                                                    {showPassword ? (
                                                        <EyeOff className="size-4" />
                                                    ) : (
                                                        <Eye className="size-4" />
                                                    )}
                                                </button>
                                            </div>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="remember"
                                render={({ field }) => (
                                    <FormItem className="flex items-center gap-2">
                                        <FormControl>
                                            <input
                                                id="remember"
                                                type="checkbox"
                                                className="size-4 rounded border-border accent-foreground"
                                                checked={!!field.value}
                                                onChange={(e) => field.onChange(e.target.checked)}
                                            />
                                        </FormControl>
                                        <Label htmlFor="remember" className="text-xs font-medium text-muted-foreground">
                                            Se souvenir de mon email
                                        </Label>
                                    </FormItem>
                                )}
                            />
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="flex w-full items-center justify-center gap-2 rounded-2xl bg-foreground py-3 text-sm font-semibold text-background shadow-sm transition disabled:cursor-not-allowed disabled:opacity-40 enabled:hover:opacity-90 enabled:active:scale-[0.99]"
                            >
                                {isLoading ? <Loader2 className="size-4 animate-spin" /> : "Se connecter"}
                            </button>
                        </form>
                    </Form>

                    <p className="mt-6 text-center text-sm text-muted-foreground">
                        Vous n'avez pas de compte ?{" "}
                        <Link
                            to="/register"
                            replace
                            className="font-semibold text-foreground underline underline-offset-4 hover:text-foreground"
                        >
                            S'inscrire
                        </Link>
                    </p>
                </div>
            </main>
        </div>
    );
}

export default LoginPage;
